/**
 * Customer Portal Dashboard Redux Slice
 * State + reducers for dashboard KPIs
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type {
  CustomerPortalDashboardState,
  DashboardData,
  ChartData,
  RankingsData,
  LiveFeedData,
  FactoryDetailData,
  EntityDetailData,
  EntityType,
  DateRange,
  DatePreset,
  FetchDashboardRequest,
  OrdersValueKpi,
  SapBalances,
} from '../domain/types'
import { DEFAULT_DATE_RANGE } from '../domain/constants'

const initialState: CustomerPortalDashboardState = {
  data: null,
  chartData: null,
  rankingsData: null,
  liveFeed: null,
  ordersValue: null,
  sapBalance: null,
  factoryDetail: {
    factoryId: null,
    factoryName: null,
    data: null,
    loading: false,
  },
  entityDetail: {
    entityType: null,
    entityId: null,
    entityName: null,
    data: null,
    loading: false,
  },
  dateRange: DEFAULT_DATE_RANGE,
  loading: {
    initial: false,
    refreshing: false,
    chart: false,
    rankings: false,
    liveFeed: false,
    ordersValue: false,
    sapBalance: false,
  },
  error: {
    message: null,
    code: undefined,
  },
  lastRefreshed: null,
}

export const customerPortalDashboardSlice = createSlice({
  name: 'customerPortalDashboard',
  initialState,
  reducers: {
    // ===== Data Setters =====
    setDashboardData: (state, action: PayloadAction<DashboardData>) => {
      state.data = action.payload
      state.loading.initial = false
      state.loading.refreshing = false
      state.error.message = null
      state.lastRefreshed = new Date().toISOString()
    },

    // ===== Action Triggers (handled by sagas) =====
    fetchDashboard: (state, _action: PayloadAction<FetchDashboardRequest | undefined>) => {
      if (!state.data) {
        state.loading.initial = true
      } else {
        state.loading.refreshing = true
      }
      state.error.message = null
    },

    fetchDashboardSuccess: (state, action: PayloadAction<DashboardData>) => {
      state.data = action.payload
      state.loading.initial = false
      state.loading.refreshing = false
      state.error.message = null
      state.lastRefreshed = new Date().toISOString()
    },

    fetchDashboardFailure: (state, action: PayloadAction<{ message: string; code?: string }>) => {
      state.error.message = action.payload.message
      state.error.code = action.payload.code
      state.loading.initial = false
      state.loading.refreshing = false
    },

    // ===== Date Range Actions =====
    setDateRange: (state, action: PayloadAction<DateRange>) => {
      state.dateRange = action.payload
    },

    setDatePreset: (state, action: PayloadAction<DatePreset>) => {
      state.dateRange = {
        preset: action.payload,
        from: null,
        to: null,
      }
    },

    setCustomDateRange: (state, action: PayloadAction<{ from: string; to: string }>) => {
      state.dateRange = {
        preset: 'custom',
        from: action.payload.from,
        to: action.payload.to,
      }
    },

    // ===== Refresh Action =====
    refreshDashboard: (state) => {
      state.loading.refreshing = true
      state.error.message = null
    },

    // ===== Chart Data Actions =====
    fetchChartData: (state, _action: PayloadAction<FetchDashboardRequest | undefined>) => {
      state.loading.chart = true
    },

    fetchChartDataSuccess: (state, action: PayloadAction<ChartData>) => {
      state.chartData = action.payload
      state.loading.chart = false
    },

    fetchChartDataFailure: (state, _action: PayloadAction<{ message: string }>) => {
      state.loading.chart = false
    },

    // ===== Rankings Actions =====
    fetchRankings: (state, _action: PayloadAction<FetchDashboardRequest | undefined>) => {
      state.loading.rankings = true
    },

    fetchRankingsSuccess: (state, action: PayloadAction<RankingsData>) => {
      state.rankingsData = action.payload
      state.loading.rankings = false
    },

    fetchRankingsFailure: (state, _action: PayloadAction<{ message: string }>) => {
      state.loading.rankings = false
    },

    // ===== Live Feed Actions =====
    fetchLiveFeed: (state) => {
      state.loading.liveFeed = true
    },

    fetchLiveFeedSuccess: (state, action: PayloadAction<LiveFeedData>) => {
      state.liveFeed = action.payload
      state.loading.liveFeed = false
    },

    fetchLiveFeedFailure: (state, _action: PayloadAction<{ message: string }>) => {
      state.loading.liveFeed = false
    },

    // ===== Factory Detail Actions =====
    fetchFactoryDetail: (state, action: PayloadAction<{ factoryId: string; factoryName: string }>) => {
      state.factoryDetail.factoryId = action.payload.factoryId
      state.factoryDetail.factoryName = action.payload.factoryName
      state.factoryDetail.loading = true
      state.factoryDetail.data = null
    },

    fetchFactoryDetailSuccess: (state, action: PayloadAction<FactoryDetailData>) => {
      state.factoryDetail.data = action.payload
      state.factoryDetail.loading = false
    },

    fetchFactoryDetailFailure: (state) => {
      state.factoryDetail.loading = false
    },

    clearFactoryDetail: (state) => {
      state.factoryDetail = { factoryId: null, factoryName: null, data: null, loading: false }
    },

    // ===== Entity Detail Actions (customer / product / city) =====
    fetchEntityDetail: (
      state,
      action: PayloadAction<{ entityType: EntityType; entityId: string; entityName: string }>
    ) => {
      state.entityDetail.entityType = action.payload.entityType
      state.entityDetail.entityId = action.payload.entityId
      state.entityDetail.entityName = action.payload.entityName
      state.entityDetail.loading = true
      state.entityDetail.data = null
    },

    fetchEntityDetailSuccess: (state, action: PayloadAction<EntityDetailData>) => {
      state.entityDetail.data = action.payload
      state.entityDetail.loading = false
    },

    fetchEntityDetailFailure: (state) => {
      state.entityDetail.loading = false
    },

    clearEntityDetail: (state) => {
      state.entityDetail = { entityType: null, entityId: null, entityName: null, data: null, loading: false }
    },

    // ===== Clear/Reset Actions =====
    // Also sets loading flags so skeletons show immediately on language change
    clearDashboard: (state) => {
      state.data = null
      state.chartData = null
      state.rankingsData = null
      state.liveFeed = null
      state.ordersValue = null
      state.sapBalance = null
      state.error.message = null
      state.lastRefreshed = null
      state.loading.initial = true
      state.loading.liveFeed = true
    },

    clearLiveFeed: (state) => {
      state.liveFeed = null
      state.loading.liveFeed = true
    },

    clearError: (state) => {
      state.error.message = null
      state.error.code = undefined
    },

    resetDashboard: () => initialState,

    // ===== Orders Value Actions =====
    fetchOrdersValue: (state) => {
      state.loading.ordersValue = true
    },

    fetchOrdersValueSuccess: (state, action: PayloadAction<OrdersValueKpi>) => {
      state.ordersValue = action.payload
      state.loading.ordersValue = false
    },

    fetchOrdersValueFailure: (state, _action: PayloadAction<{ message: string }>) => {
      state.loading.ordersValue = false
    },

    // ===== SAP Balance Actions =====
    fetchSapBalance: (state) => {
      state.loading.sapBalance = true
    },

    fetchSapBalanceSuccess: (state, action: PayloadAction<SapBalances>) => {
      state.sapBalance = action.payload
      state.loading.sapBalance = false
    },

    fetchSapBalanceFailure: (state) => {
      state.loading.sapBalance = false
    },
  },
})

export const customerPortalDashboardActions = customerPortalDashboardSlice.actions
export const customerPortalDashboardReducer = customerPortalDashboardSlice.reducer
